import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { theme } from "@main/theme";
import { useThemeMode } from "@main/context/ThemeContext";
import i18n from "@main/i18n";
import { isFullyTranslated } from "@main/lib/languages";
import { useCurrentUser } from "@main/auth/useCurrentUser";
import { clearLocalSession, hasLocalSession as hasLocalSessionNow, onLocalSessionEnded } from "@main/api/client";
import { LoginPage } from "@main/pages/Login";
import { InviteRedeemPage } from "@main/pages/InviteRedeem";
import { SupplierQuestionnairePage } from "@main/pages/SupplierQuestionnaire";
import { SubmissionSharePage } from "@main/pages/SubmissionShare";
import { AssuranceSharePage } from "@main/pages/AssuranceShare";
import { SignedOutPage } from "@main/pages/SignedOut";
import { PrivacyPolicyPage } from "@main/pages/PrivacyPolicy";
import { MfaEnrolPage } from "@main/pages/MfaEnrol";
import { ForgotPasswordPage, ResetPasswordPage } from "@main/pages/ResetPassword";
import { JsyClientLayout } from "./JsyClientLayout";
import { JsyOnboardingPage } from "./pages/JsyOnboarding";

// ── Jersey edition app (jsy.abacassecurity.com) ───────────────────────────
// The same sign-in, invitation, MFA and public pages as the main platform, then
// the Jersey client shell. The edition is the organisation's, read from the API
// (GET /auth/me → edition / editionUrl), never from this host: a member of a
// standard-edition organisation is sent to the main platform, and consultancy
// staff and auditors — who manage Jersey clients from the existing consultant
// and auditor screens — are sent there too.

// The Jersey sign-in heading: Jèrriais for "Welcome to Abacas Security".
// Client sign-in only: consultants and auditors manage Jersey clients from the main platform.
const JSY_LOGIN = { heading: "Séyiz l'beinv'nu Abacas Security", tagline: "Security compliance for Jersey Businesses", clientOnly: true };

const MAIN_APP_URL = (import.meta.env.VITE_MAIN_APP_URL ?? "https://app.abacassecurity.com").replace(/\/+$/, "");

function FullScreenMessage({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", background: theme.color.bg, color: theme.color.muted, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: theme.font, fontSize: 13 }}>
      <div style={{ textAlign: "center", maxWidth: 460, padding: "0 16px", lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

/** Sends the browser to another frontend, keeping the path the user asked for. */
function SendElsewhere({ base, title, body }: { base: string; title: string; body: string }) {
  const location = useLocation();
  useEffect(() => {
    const t = setTimeout(() => window.location.replace(`${base}${location.pathname}${location.search}`), 1500);
    return () => clearTimeout(t);
  }, [base, location.pathname, location.search]);
  return (
    <FullScreenMessage>
      <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>{title}</div>
      <div style={{ marginBottom: 14 }}>{body}</div>
      <a href={base} style={{ color: theme.color.accent, fontWeight: 600 }}>Continue to {new URL(base).host}</a>
    </FullScreenMessage>
  );
}

export function JsyApp() {
  // Re-render every page when the colour-blind theme is switched (lib/colourBlind.ts).
  useThemeMode();
  const location = useLocation();
  const navigate = useNavigate();
  const isEntraAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const hasLocalSession = hasLocalSessionNow();
  const { user, loading, organisationNotRegistered, signInRefused, refetch } = useCurrentUser();

  const handleAutoSignOut = useCallback(() => {
    clearLocalSession();
    navigate("/signedout?reason=inactivity");
    instance.logoutRedirect({ onRedirectNavigate: () => false });
  }, [navigate, instance]);

  useEffect(() => onLocalSessionEnded(() => {
    instance.logoutRedirect({ onRedirectNavigate: () => false });
    navigate("/signedout");
  }), [navigate, instance]);

  useEffect(() => {
    if (isFullyTranslated(user?.language) && user.language !== i18n.language) void i18n.changeLanguage(user.language);
  }, [user?.language]);

  // Pages that render whatever the session.
  switch (location.pathname) {
    case "/signedout": return <SignedOutPage />;
    case "/invite": return <InviteRedeemPage />;
    case "/supplier-questionnaire": return <SupplierQuestionnairePage />;
    case "/submission-share": return <SubmissionSharePage />;
    case "/assurance-share": return <AssuranceSharePage />;
    case "/privacy-policy": return <PrivacyPolicyPage />;
    case "/forgot-password": return <ForgotPasswordPage />;
    case "/reset-password": return <ResetPasswordPage />;
  }

  if (!isEntraAuthenticated && !hasLocalSession && inProgress !== InteractionStatus.HandleRedirect) {
    return <LoginPage onLocalLogin={refetch} {...JSY_LOGIN} />;
  }
  if (loading) return <FullScreenMessage>Loading your account…</FullScreenMessage>;
  if (signInRefused) {
    return (
      <FullScreenMessage>
        <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>Sign in with your work account</div>
        {signInRefused}
      </FullScreenMessage>
    );
  }
  if (organisationNotRegistered) {
    return (
      <FullScreenMessage>
        <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>Access by invitation only</div>
        Your organisation isn't registered on Abacas Jersey. Please contact your consultant to receive an invitation.
      </FullScreenMessage>
    );
  }
  if (!user) return <LoginPage onLocalLogin={refetch} {...JSY_LOGIN} />;

  if (user.mfaEnrolmentRequired) return <MfaEnrolPage user={user} onDone={refetch} />;

  if (user.role !== "client") {
    return (
      <SendElsewhere
        base={MAIN_APP_URL}
        title="Jersey clients are managed from the main platform"
        body="Consultants and assessors work with Jersey edition clients from the existing consultant and auditor screens. Taking you there now."
      />
    );
  }

  if (user.edition !== "jersey") {
    return (
      <SendElsewhere
        base={user.editionUrl ?? MAIN_APP_URL}
        title="Your organisation is on the main platform"
        body="Abacas Jersey is for organisations on the Jersey edition. Taking you to your compliance portal."
      />
    );
  }

  if (!user.onboardingComplete) return <JsyOnboardingPage onComplete={refetch} />;

  return <JsyClientLayout user={user} onAutoSignOut={handleAutoSignOut} />;
}
